import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";

import {
  selectHeaderAnimationInProgress,
  selectIsHeaderCollapsed,
  setHeaderAnimationInProgress,
  setIsHeaderCollapsed
} from "./patient-header-slice";

const PatientHeaderContext = React.createContext<{
  isHeaderCollapsed: boolean | undefined;
  headerAnimationInProgress: string | undefined;
  collapseHeader: () => void;
  expandHeader: () => void;
  onNewTouchpointCloseModalCallback: () => void;
  setNewTouchpointCloseModalCallback: (callback?: () => void) => void;
} | null>(null);

type PatientHeaderProviderProps = {
  children?: React.ReactNode;
};

export function PatientHeaderProvider({
  children
}: PatientHeaderProviderProps) {
  const dispatch = useAppDispatch();

  const isHeaderCollapsed = useAppSelector(selectIsHeaderCollapsed);
  const headerAnimationInProgress = useAppSelector(
    selectHeaderAnimationInProgress
  );

  useEffect(() => {
    dispatch(setIsHeaderCollapsed(false));
    dispatch(setHeaderAnimationInProgress(""));
  }, [dispatch]);

  const collapseHeader = useCallback(() => {
    dispatch(setHeaderAnimationInProgress("closing"));

    setTimeout(() => {
      dispatch(setIsHeaderCollapsed(true));
      dispatch(setHeaderAnimationInProgress(""));
    }, 250);
  }, [dispatch]);

  const expandHeader = useCallback(() => {
    dispatch(setHeaderAnimationInProgress("expanding"));

    setTimeout(() => {
      dispatch(setIsHeaderCollapsed(false));
      dispatch(setHeaderAnimationInProgress(""));
    }, 200);
  }, [dispatch]);

  const newTouchpointCloseModalCallbackRef = useRef<any>();

  const onNewTouchpointCloseModalCallback = useCallback(() => {
    if (newTouchpointCloseModalCallbackRef.current) {
      newTouchpointCloseModalCallbackRef.current();
    }
  }, []);

  const setNewTouchpointCloseModalCallback = useCallback(
    (callback?: () => void) => {
      newTouchpointCloseModalCallbackRef.current = callback;
    },
    []
  );

  const value = useMemo(
    () => ({
      isHeaderCollapsed,
      headerAnimationInProgress,
      collapseHeader,
      expandHeader,
      onNewTouchpointCloseModalCallback,
      setNewTouchpointCloseModalCallback
    }),
    [
      isHeaderCollapsed,
      headerAnimationInProgress,
      collapseHeader,
      expandHeader,
      onNewTouchpointCloseModalCallback,
      setNewTouchpointCloseModalCallback
    ]
  );

  return (
    <PatientHeaderContext.Provider value={value}>
      {children}
    </PatientHeaderContext.Provider>
  );
}

export default function usePatientHeader() {
  const context = React.useContext(PatientHeaderContext);
  if (!context) {
    throw new Error(
      "usePatientHeader must be used within a PatientHeaderProvider"
    );
  }

  const {
    isHeaderCollapsed,
    headerAnimationInProgress,
    collapseHeader,
    expandHeader,

    onNewTouchpointCloseModalCallback,
    setNewTouchpointCloseModalCallback
  } = context;

  return {
    isHeaderCollapsed,
    headerAnimationInProgress,
    collapseHeader,
    expandHeader,
    onNewTouchpointCloseModalCallback,
    setNewTouchpointCloseModalCallback
  };
}
